import apiClient from './index';

export interface ImportJobStatus {
  job_id: string;
  filename: string;
  source_name: string;
  format_type: string;
  status: 'pending' | 'validating' | 'processing' | 'completed' | 'failed';
  progress: number;
  total_rows: number;
  current_row: number;
  products_inserted: number;
  products_updated: number;
  products_skipped: number;
  errors: string[];
  error_count: number;
  message: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface ImportQueueResponse {
  status: 'queued' | 'error';
  message: string;
  job_id?: string;
  source_name?: string;
  detected_format?: string;
}

export interface ImportStats {
  products: number;
  manufacturers: number;
  categories: number;
  features: number;
  attributes: number;
  skus: number;
}

export interface ETLStatus {
  status: 'idle' | 'starting' | 'downloading' | 'unzipping' | 'importing' | 'completed' | 'failed';
  message: string;
  phase: string;
  files_downloaded: number;
  files_unzipped: number;
  current_file: string;
  current_delta: string;
  started_at: string | null;
  last_activity: string | null;
  errors: string[];
  warnings: string[];  // Non-critical errors like email failures
  is_running: boolean;
  // Import statistics
  stats_before: ImportStats | null;
  stats_after: ImportStats | null;
  stats_change: ImportStats | null;
}

export interface ETLStartResponse {
  status: 'started' | 'already_running' | 'error';
  message: string;
  job?: ETLStatus;
}

export async function uploadCSV(
  file: File,
  format: string = 'auto',
  sourceName?: string
): Promise<ImportQueueResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('format', format);
  if (sourceName) {
    formData.append('source_name', sourceName);
  }

  const response = await apiClient.post<ImportQueueResponse>(
    '/spex/plus/import/csv/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

export async function getJobStatus(jobId: string): Promise<ImportJobStatus> {
  const response = await apiClient.get<ImportJobStatus>(
    `/spex/plus/import/status/${jobId}`
  );
  return response.data;
}

export async function listJobs(limit: number = 20): Promise<ImportJobStatus[]> {
  const response = await apiClient.get<ImportJobStatus[]>(
    '/spex/plus/import/jobs',
    { params: { limit } }
  );
  return response.data;
}

export async function runEtlUpdate(): Promise<ETLStartResponse> {
  const response = await apiClient.post<ETLStartResponse>('/spex/plus/update');
  return response.data;
}

export async function getEtlStatus(): Promise<ETLStatus> {
  const response = await apiClient.get<ETLStatus>('/spex/plus/etl/status');
  return response.data;
}

export async function getDatabaseStats(): Promise<ImportStats> {
  const response = await apiClient.get<ImportStats>('/spex/plus/stats');
  return response.data;
}
